import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Eye, CheckCircle, XCircle, User, Phone, Mail, MapPin, BookOpen } from 'lucide-react';

export default function Applications() {
  const { isEditor, canEdit, staff } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [view, setView] = useState(null);
  const [notes, setNotes] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => { loadApps(); }, [filter]);
  // a new online application, or a status change by another staff member
  useLiveRefresh(['student_applications'], () => loadApps(true));

  async function loadApps(silent) {
    if (silent !== true) setLoading(true);
    let q = supabase.from('student_applications').select('*').order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('status', filter);
    const { data } = await q;
    setApps(data || []);
    setLoading(false);
  }

  async function updateStatus(id, status) {
    await supabase.from('student_applications').update({
      status,
      reviewed_by: staff?.full_name || 'Admin',
      reviewed_at: new Date().toISOString(),
      notes,
    }).eq('id', id);
    toast.success(`Application ${status}`);
    setView(null);
    setNotes('');
    loadApps();
  }

  async function convertToStudent(app) {
    setConverting(true);
    // Check for duplicate
    const { data: existing } = await supabase.from('students')
      .select('id').eq('contact_number', app.contact_number).single();
    if (existing) {
      toast.error('Student with this contact number already exists!');
      setConverting(false);
      return;
    }
    // Insert into students
    const { error } = await supabase.from('students').insert({
      full_name: app.full_name,
      dob: app.date_of_birth,
      contact_number: app.contact_number,
      whatsapp_number: app.whatsapp_number || app.contact_number,
      email: app.email,
      place: app.address,
      pincode: app.pincode,
      district: app.district,
      state: app.state,
      known_location: app.known_location,
      guardian_name: app.guardian_name,
      guardian_contact: app.guardian_contact,
      college_name: app.college_name,
      academic_qualification: app.academic_qualification,
      professional_qualification: app.professional_qualification || null,
      course_name: app.course_name,
      centre: app.centre,
      training_mode: app.training_mode,
      is_active: true,
      student_status: 'Continuing',
      joining_date: new Date().toISOString().split('T')[0],
    });
    if (error) { toast.error('Error: ' + error.message); setConverting(false); return; }
    // Mark application as converted
    await supabase.from('student_applications').update({
      status: 'converted',
      reviewed_by: staff?.full_name || 'Admin',
      reviewed_at: new Date().toISOString(),
    }).eq('id', app.id);
    toast.success('✅ Student profile created successfully!');
    setView(null);
    setConverting(false);
    loadApps();
  }

  const statusColor = { pending:'#f59e0b', approved:'#22c55e', rejected:'#ef4444', converted:'#8b5cf6' };
  const statusBg    = { pending:'#fef3c7', approved:'#dcfce7', rejected:'#fee2e2', converted:'#ede9fe' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">📋 Student Applications</h1>
          <p className="page-subtitle">Review and approve student applications submitted online</p>
        </div>
        <a href="/apply" target="_blank" rel="noreferrer"
          style={{padding:'8px 16px',background:'var(--accent)',color:'#fff',borderRadius:8,textDecoration:'none',fontSize:13,fontWeight:600}}>
          🔗 Application Form Link
        </a>
      </div>

      {/* Stats */}
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        {['pending','approved','rejected','converted','all'].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{
            padding:'8px 16px',borderRadius:8,border:'none',cursor:'pointer',fontWeight:600,fontSize:13,
            background:filter===s?statusBg[s]||'var(--accent-light)':'var(--surface)',
            color:filter===s?statusColor[s]||'var(--accent)':'var(--text2)',
          }}>{s.charAt(0).toUpperCase()+s.slice(1)}</button>
        ))}
      </div>

      {loading ? <div className="loading"><div className="spinner"/></div> : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>#</th><th>Name</th><th>Contact</th><th>Course</th><th>Centre</th>
              <th>Qualification</th><th>Applied On</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {apps.length === 0
                ? <tr><td colSpan={9} style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No {filter} applications</td></tr>
                : apps.map((a,i) => (
                  <tr key={a.id}>
                    <td style={{color:'var(--text3)',fontSize:12}}>{i+1}</td>
                    <td>
                      <div style={{fontWeight:600}}>{a.full_name}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{a.email||'—'}</div>
                    </td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{a.contact_number}</td>
                    <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:12,background:'var(--accent-light)',color:'var(--accent)',fontWeight:600}}>{a.course_name||'—'}</span></td>
                    <td style={{fontSize:12}}>{a.centre||'—'}</td>
                    <td style={{fontSize:12}}>{a.academic_qualification||'—'}</td>
                    <td style={{fontSize:11,color:'var(--text3)'}}>{new Date(a.created_at).toLocaleDateString('en-IN')}</td>
                    <td>
                      <span style={{fontSize:11,padding:'3px 10px',borderRadius:12,fontWeight:700,
                        background:statusBg[a.status]||'#f1f5f9',color:statusColor[a.status]||'#64748b'}}>
                        {a.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setView(a);setNotes(a.notes||'');}} title="View"><Eye size={14}/></button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {view && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setView(null)}>
          <div className="modal modal-lg" style={{maxWidth:700}}>
            <div className="modal-header">
              <h3>📋 Application — {view.full_name}</h3>
              <button className="modal-close" onClick={()=>setView(null)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight:'65vh',overflowY:'auto'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <Section title="👤 Personal" items={[
                  ['Full Name', view.full_name],
                  ['DOB', view.date_of_birth||'—'],
                  ['Gender', view.gender||'—'],
                  ['Contact', view.contact_number],
                  ['WhatsApp', view.whatsapp_number||'—'],
                  ['Email', view.email||'—'],
                ]}/>
                <Section title="📍 Address" items={[
                  ['Address', view.address||'—'],
                  ['Location', view.known_location||'—'],
                  ['Pincode', view.pincode||'—'],
                  ['District', view.district||'—'],
                  ['State', view.state||'—'],
                ]}/>
                <Section title="👨‍👩‍👦 Guardian" items={[
                  ['Guardian Name', view.guardian_name||'—'],
                  ['Guardian Contact', view.guardian_contact||'—'],
                ]}/>
                <Section title="🎓 Academic" items={[
                  ['College', view.college_name||'—'],
                  ['Qualification', view.academic_qualification||'—'],
                  ['Professional Qual.', view.professional_qualification||'—'],
                ]}/>
                <Section title="📚 Course" items={[
                  ['Course', view.course_name||'—'],
                  ['Centre', view.centre||'—'],
                  ['Mode', view.training_mode||'—'],
                ]}/>
                <div>
                  <div style={{fontWeight:700,fontSize:12,color:'var(--text3)',marginBottom:8,textTransform:'uppercase'}}>📝 Notes</div>
                  <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={4}
                    style={{width:'100%',padding:'8px 12px',borderRadius:8,border:'1px solid var(--border)',fontSize:13,resize:'vertical'}}
                    placeholder="Add review notes..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setView(null)}>Close</button>
              {canEdit('applications') && view.status === 'pending' && (<>
                <button className="btn btn-secondary" style={{background:'#ef4444',color:'#fff',border:'none'}}
                  onClick={()=>updateStatus(view.id,'rejected')}>✗ Reject</button>
                <button className="btn btn-secondary" style={{background:'#22c55e',color:'#fff',border:'none'}}
                  onClick={()=>updateStatus(view.id,'approved')}>✓ Approve</button>
              </>)}
              {canEdit('applications') && (view.status === 'approved' || view.status === 'pending') && (
                <button className="btn btn-primary" onClick={()=>convertToStudent(view)} disabled={converting}>
                  {converting ? '⏳ Creating...' : '👤 Create Student Profile'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div style={{background:'var(--surface2)',borderRadius:10,padding:14}}>
      <div style={{fontWeight:700,fontSize:12,color:'var(--text3)',marginBottom:10,textTransform:'uppercase'}}>{title}</div>
      {items.map(([k,v]) => (
        <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:13}}>
          <span style={{color:'var(--text3)',minWidth:120}}>{k}</span>
          <span style={{fontWeight:500,textAlign:'right'}}>{v}</span>
        </div>
      ))}
    </div>
  );
}
